/**
 * Live updates (ADR 0008): one server-sent-events connection per page, shared by every screen.
 * `useLive` gives a screen the changes on the channels it shows and a word when the connection
 * comes back (the screen re-fetches then). No business logic: the app names the URL and the
 * channels; the browser reconnects on its own and sends `Last-Event-ID`.
 */
import { useEffect, useRef } from 'react';

export interface LiveChange {
  channel: string;
  subject: string | null;
  event: string;
  at: string;
}
export type LiveSignal = { kind: 'change'; change: LiveChange } | { kind: 'reconnected' };
type Handler = (s: LiveSignal) => void;

interface Stream {
  source: EventSource;
  handlers: Set<Handler>;
  dropped: boolean;
}
const streams = new Map<string, Stream>();

function open(url: string): Stream {
  const have = streams.get(url);
  if (have) return have;
  const source = new EventSource(url, { withCredentials: true });
  const stream: Stream = { source, handlers: new Set(), dropped: false };
  source.addEventListener('change', (ev) => {
    try {
      const change = JSON.parse((ev as MessageEvent<string>).data) as LiveChange;
      for (const h of stream.handlers) h({ kind: 'change', change });
    } catch {
      /* a malformed frame is nobody's business */
    }
  });
  source.onerror = () => {
    stream.dropped = true;
  };
  source.onopen = () => {
    if (!stream.dropped) return;
    stream.dropped = false;
    for (const h of stream.handlers) h({ kind: 'reconnected' });
  };
  streams.set(url, stream);
  return stream;
}

/** Close the page's connection (sign-out). */
export function closeLive(url: string): void {
  const s = streams.get(url);
  if (!s) return;
  s.source.close();
  streams.delete(url);
}

/** True when `channel` is one of `prefixes` or begins with one followed by a dot. */
export function onChannel(channel: string, prefixes: readonly string[]): boolean {
  return prefixes.some((p) => channel === p || channel.startsWith(`${p}.`));
}

/**
 * Subscribe while mounted. `channels` are prefixes ("money", "people.person"); a change on one
 * of them, or the connection coming back, calls `onSignal`. Pass `null` for no connection (signed out).
 */
export function useLive(
  url: string | null,
  channels: readonly string[],
  onSignal: (s: LiveSignal) => void,
): void {
  const latest = useRef(onSignal);
  latest.current = onSignal;
  const key = channels.join('|');
  useEffect(() => {
    if (!url) return undefined;
    const prefixes = key ? key.split('|') : [];
    const stream = open(url);
    const handler: Handler = (s) => {
      if (s.kind === 'reconnected' || onChannel(s.change.channel, prefixes)) latest.current(s);
    };
    stream.handlers.add(handler);
    return () => {
      stream.handlers.delete(handler);
    };
  }, [url, key]);
}
